import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Plus, Trash2, Check, Loader2, Send, Server } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "hr", label: "HR — appointment / warning / payroll / leave" },
  { value: "operations", label: "Operations — alerts / announcements / reminders" },
  { value: "general", label: "General — any communication" },
];

export default function EmailSettings() {
  const [senders, setSenders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [newSender, setNewSender] = useState({ sender_email: "", label: "", category: "hr" });
  const [busy, setBusy] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [test, setTest] = useState({ sender_email: "", to: "", subject: "Happy Retreats — email test", html_body: "<p>This is a test from your HRMS.</p>" });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [smtpOpen, setSmtpOpen] = useState(false);
  const [smtp, setSmtp] = useState({ sender_email: "", label: "", category: "hr", host: "", port: 587, username: "", password: "", use_tls: true, use_ssl: false });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        api.get("/email/senders"),
        api.get("/email/settings"),
      ]);
      setSenders(s.data);
      setSettings(st.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load email config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Show success toast on OAuth return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") {
      toast.success("Mailbox connected successfully");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const updateSetting = async (patch) => {
    const prev = settings;
    setSettings({ ...settings, ...patch });
    try {
      const { data } = await api.put("/email/settings", patch);
      setSettings(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      setSettings(prev);
    }
  };

  const startConnect = async () => {
    if (!newSender.sender_email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/email/oauth/start", newSender);
      // Open Google consent in a new tab so admin keeps the HRMS open
      window.location.href = data.oauth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start OAuth");
      setBusy(false);
    }
  };

  const disconnect = async (email) => {
    try {
      await api.delete(`/email/senders/${encodeURIComponent(email)}`);
      toast.success(`Disconnected ${email}`);
      setConfirmDelete(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const connectSmtp = async () => {
    if (!smtp.sender_email.includes("@")) { toast.error("Enter a valid sender email"); return; }
    if (!smtp.host.trim()) { toast.error("Enter the SMTP host"); return; }
    setBusy(true);
    try {
      await api.post("/email/smtp", { ...smtp, port: Number(smtp.port) || 587 });
      toast.success(`SMTP mailbox ${smtp.sender_email} saved`);
      setSmtpOpen(false);
      setSmtp({ sender_email: "", label: "", category: "hr", host: "", port: 587, username: "", password: "", use_tls: true, use_ssl: false });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save SMTP mailbox");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {    if (!test.sender_email || !test.to) { toast.error("Pick sender + recipient"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/email/test", test);
      if (data.status === "sent") toast.success("Test email sent");
      else toast.error(data.error || "Send failed — check Email Logs");
      setTestOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const hrSenderOptions = senders.filter((s) => s.active);
  const opsSenderOptions = senders.filter((s) => s.active);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Communications</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Email Settings</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Connect Google Workspace mailboxes (hr@, kai@, etc.) via OAuth, or bring your own server with <strong>Custom SMTP</strong>. Every system email is sent via the chosen sender.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTestOpen(true)} disabled={senders.length === 0} data-testid="email-test">
            <Send className="w-4 h-4 mr-1" /> Send test email
          </Button>
          <Button variant="outline" onClick={() => setSmtpOpen(true)} data-testid="email-connect-smtp">
            <Server className="w-4 h-4 mr-1" /> Connect SMTP
          </Button>
          <Button onClick={() => setConnectOpen(true)} className="bg-zinc-950 hover:bg-zinc-800" data-testid="email-connect-new">
            <Plus className="w-4 h-4 mr-1" /> Connect mailbox
          </Button>
        </div>
      </div>

      {/* Connected senders */}
      <section className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <header className="px-4 pt-4">
          <div className="label-overline flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Connected Mailboxes</div>
        </header>
        <table className="w-full text-sm mt-2 min-w-[640px]">
          <thead className="bg-zinc-50 border-y border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-2 px-4">Mailbox</th>
              <th className="py-2 px-3">Provider</th>
              <th className="py-2 px-3">Category</th>
              <th className="py-2 px-3">Connected by</th>
              <th className="py-2 px-3">When</th>
              <th className="py-2 px-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {senders.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-zinc-400">
                No mailboxes connected yet. Click <em>Connect mailbox</em> (Google) or <em>Connect SMTP</em> to add one.
              </td></tr>
            )}
            {senders.map((s) => (
              <tr key={s.sender_email} className="border-b border-zinc-100 last:border-0" data-testid={`email-sender-${s.sender_email}`}>
                <td className="py-2.5 px-4">
                  <div className="font-medium">{s.sender_email}</div>
                  <div className="text-[10px] text-zinc-500">{s.label}</div>
                </td>
                <td className="py-2.5 px-3">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.provider === "smtp" ? "bg-indigo-50 text-indigo-700" : "bg-emerald-50 text-emerald-700"}`}
                    data-testid={`email-sender-provider-${s.sender_email}`}
                  >
                    {s.provider === "smtp" ? <Server className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                    {s.provider === "smtp" ? "Custom SMTP" : "Google"}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-xs capitalize">{s.category}</td>
                <td className="py-2.5 px-3 text-xs">{s.connected_by_name || "—"}</td>
                <td className="py-2.5 px-3 text-xs text-zinc-500">{s.connected_at ? new Date(s.connected_at).toLocaleString() : "—"}</td>
                <td className="py-2.5 px-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => { setTest({ ...test, sender_email: s.sender_email }); setTestOpen(true); }}
                      className="p-1.5 hover:bg-zinc-100 hover:text-zinc-900 rounded text-zinc-500"
                      title="Send a test email from this mailbox"
                      data-testid={`email-test-row-${s.sender_email}`}
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(s.sender_email)}
                      className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded"
                      title="Disconnect this mailbox"
                      data-testid={`email-disconnect-${s.sender_email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Sender routing */}
      <section className="bg-white border border-zinc-200 rounded-md p-4">
        <div className="label-overline mb-2">Sender Routing</div>
        <p className="text-xs text-zinc-500 mb-3">Which mailbox should send each category of email.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>HR communications (payslips, warnings, leave decisions)</Label>
            <Select
              value={settings.hr_sender_email || ""}
              onValueChange={(v) => updateSetting({ hr_sender_email: v || null })}
            >
              <SelectTrigger data-testid="hr-sender-select"><SelectValue placeholder="Pick a connected mailbox" /></SelectTrigger>
              <SelectContent>
                {hrSenderOptions.map((s) => (
                  <SelectItem key={s.sender_email} value={s.sender_email}>{s.sender_email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Operations / system notices (announcements, reminders)</Label>
            <Select
              value={settings.operations_sender_email || ""}
              onValueChange={(v) => updateSetting({ operations_sender_email: v || null })}
            >
              <SelectTrigger data-testid="ops-sender-select"><SelectValue placeholder="Pick a connected mailbox" /></SelectTrigger>
              <SelectContent>
                {opsSenderOptions.map((s) => (
                  <SelectItem key={s.sender_email} value={s.sender_email}>{s.sender_email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Auto-send disabled — every system email is manual via a "Send Email" button. */}
      <section className="bg-white border border-zinc-200 rounded-md p-4">
        <div className="label-overline mb-2">Sending mode</div>
        <p className="text-xs text-zinc-500">
          Every email is sent <strong>manually</strong>. Users with the <em>Send Email</em> permission see a
          {" "}<em>Send email</em> button on Payroll rows, Warnings, Appreciations and staff profiles.
          Browse <a href="/email/templates" className="text-zinc-900 underline">Email Templates</a> to manage the
          subject + body presets that those buttons load by default.
        </p>
      </section>

      {/* Automatic notifications */}
      <section className="bg-white border border-zinc-200 rounded-md p-4" data-testid="auto-notifications-section">
        <div className="label-overline mb-2">Automatic notifications</div>
        <p className="text-xs text-zinc-500 mb-3">
          A few notification types fire automatically. They're delivered via in-app + browser push;
          email goes out only when toggled on AND the operations mailbox is connected.
        </p>
        <div className="flex items-start justify-between gap-4 py-2 border-t border-zinc-100">
          <div>
            <div className="text-sm font-semibold">Shift assignment / removal</div>
            <p className="text-[11px] text-zinc-500 max-w-md">
              Email the employee when a manager assigns, updates, or removes their shift. In-app + push are always sent.
            </p>
          </div>
          <Switch
            checked={!!settings.auto_shift}
            onCheckedChange={(v) => updateSetting({ auto_shift: v })}
            data-testid="auto-shift-toggle"
          />
        </div>
      </section>

      {/* Retention */}
      <section className="bg-white border border-zinc-200 rounded-md p-4">
        <div className="label-overline">Email log retention</div>
        <p className="text-xs text-zinc-500 mt-1 mb-2">
          Logs are kept forever by default. You can manually purge older logs whenever needed.
        </p>
        <PurgeRow />
      </section>

      {/* Connect dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent data-testid="email-connect-dialog">
          <DialogHeader>
            <DialogTitle>Connect a Workspace mailbox</DialogTitle>
            <DialogDescription>
              You'll be redirected to Google. Sign in with the mailbox below and grant the <em>Send email</em> permission.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mailbox email <span className="text-red-500">*</span></Label>
              <Input
                value={newSender.sender_email}
                onChange={(e) => setNewSender({ ...newSender, sender_email: e.target.value })}
                placeholder="e.g. hr@happyretreats.in"
                data-testid="email-connect-input"
              />
            </div>
            <div>
              <Label>Display label</Label>
              <Input
                value={newSender.label}
                onChange={(e) => setNewSender({ ...newSender, label: e.target.value })}
                placeholder="e.g. Happy Retreats HR"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Shown as the "From:" name in recipients' inboxes.</p>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newSender.category} onValueChange={(v) => setNewSender({ ...newSender, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={startConnect} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="email-connect-go">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Continue to Google
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMTP connect dialog */}
      <Dialog open={smtpOpen} onOpenChange={setSmtpOpen}>
        <DialogContent data-testid="email-smtp-dialog">
          <DialogHeader>
            <DialogTitle>Connect a custom SMTP mailbox</DialogTitle>
            <DialogDescription>
              Use your own mail server or any provider that supports SMTP. We test the connection before saving; credentials are encrypted at rest and never shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sender email <span className="text-red-500">*</span></Label>
                <Input
                  value={smtp.sender_email}
                  onChange={(e) => setSmtp({ ...smtp, sender_email: e.target.value })}
                  placeholder="e.g. hr@happyretreats.in"
                  data-testid="smtp-email-input"
                />
              </div>
              <div>
                <Label>Display label</Label>
                <Input
                  value={smtp.label}
                  onChange={(e) => setSmtp({ ...smtp, label: e.target.value })}
                  placeholder="e.g. Happy Retreats HR"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>SMTP host <span className="text-red-500">*</span></Label>
                <Input
                  value={smtp.host}
                  onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                  placeholder="e.g. smtp.gmail.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div>
                <Label>Port</Label>
                <Input
                  type="number"
                  value={smtp.port}
                  onChange={(e) => setSmtp({ ...smtp, port: e.target.value })}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username</Label>
                <Input
                  value={smtp.username}
                  onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                  placeholder="usually the email address"
                  data-testid="smtp-username-input"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={smtp.password}
                  onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="smtp-password-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={smtp.use_tls}
                  onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v, use_ssl: v ? false : smtp.use_ssl })}
                  data-testid="smtp-tls-toggle"
                />
                <Label className="text-xs">STARTTLS (port 587)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={smtp.use_ssl}
                  onCheckedChange={(v) => setSmtp({ ...smtp, use_ssl: v, use_tls: v ? false : smtp.use_tls })}
                  data-testid="smtp-ssl-toggle"
                />
                <Label className="text-xs">SSL/TLS (port 465)</Label>
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={smtp.category} onValueChange={(v) => setSmtp({ ...smtp, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={connectSmtp} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="smtp-save-go">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Save SMTP mailbox
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test send dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send a test email</DialogTitle>
            <DialogDescription>
              Confirms the connected mailbox is sending correctly. The send is logged the same as any system email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Select value={test.sender_email} onValueChange={(v) => setTest({ ...test, sender_email: v })}>
                <SelectTrigger><SelectValue placeholder="Pick a connected mailbox" /></SelectTrigger>
                <SelectContent>
                  {senders.map((s) => <SelectItem key={s.sender_email} value={s.sender_email}>{s.sender_email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Input value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} placeholder="recipient@example.com" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={test.subject} onChange={(e) => setTest({ ...test, subject: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendTest} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              After disconnecting, the system cannot send email from <strong>{confirmDelete}</strong> until you reconnect it.
              Existing logs are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => disconnect(confirmDelete)} className="bg-red-600 hover:bg-red-700">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PurgeRow() {
  const [days, setDays] = useState(180);
  const [busy, setBusy] = useState(false);
  const purge = async () => {
    if (!window.confirm(`Purge all email logs older than ${days} days? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/email/logs/purge?older_than_days=${days}`);
      toast.success(`Deleted ${data.deleted} log(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2">
      <div>
        <Label className="text-xs">Purge logs older than</Label>
        <Input type="number" min="30" value={days} onChange={(e) => setDays(Number(e.target.value) || 30)} className="w-32" data-testid="email-purge-days" />
      </div>
      <Button variant="outline" onClick={purge} disabled={busy} data-testid="email-purge-go">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Purge
      </Button>
    </div>
  );
}
